import { onError } from '@apollo/client/link/error'
import { useAuthStore } from '~/stores/authStore'

/**
 * Apollo error link.
 *
 * Centralises how the app reacts to auth failures so the rest of the
 * code can just `await client.query(...)` without each call site
 * re-implementing "what if the session expired".
 *
 * Behaviour:
 *  - `UNAUTHENTICATED` / `KS_ACCESS_DENIED` (graphql) or network 401:
 *      treat as session-expired. Clear auth, send the user to the
 *      login page with `?redirect=<currentPath>&reason=session_expired`
 *      so the login screen can show the "your session expired" banner
 *      and bounce them back where they were after re-auth.
 *  - `FORBIDDEN` (graphql): the user IS authenticated but doesn't have
 *      access. Send them to the unauthorized page with the path they
 *      were trying to reach so it can offer a "go back" affordance.
 *
 * Suppression rules — we DON'T fire the session-expired flow when:
 *  - the failing operation is the auth-restore query (`AuthenticatedUser`):
 *      the route middleware already handles that case by calling
 *      `clearAuth` and routing to login — letting errorLink also chime
 *      in would race and double-navigate.
 *  - the failing operation is `EndSession` or `authStore.isLoggingOut`:
 *      logout often races with an already-invalid session; the user
 *      doesn't need a "session expired" notice on top of their own
 *      logout flow.
 *  - we're already on `/account/login`: avoid redirect loops if the
 *      login screen itself fires an authenticated query somehow.
 */

// Operation names the error link should leave alone. Keep this in
// sync with the gql `query`/`mutation` names in `graphql/auth/`.
const AUTH_OP_SKIP = new Set(['AuthenticatedUser', 'EndSession'])

function isOnLoginPage() {
  if (typeof window === 'undefined') return false

  return window.location.pathname.startsWith('/account/login')
}

function buildExpiredRedirect() {
  const currentPath = window.location.pathname + window.location.search
  const safePath = currentPath && currentPath !== '/account/login' ? currentPath : '/follow-ups'

  return {
    path: '/account/login',
    query: { redirect: safePath, reason: 'session_expired' },
  }
}

function handleSessionExpired(authStore) {
  if (!import.meta.client) return
  if (isOnLoginPage()) return
  if (authStore.isLoggingOut) return

  if (authStore.sessionToken || authStore.isAuthenticated) {
    authStore.clearAuth()
  }

  navigateTo(buildExpiredRedirect(), { replace: true })
}

export default defineNuxtPlugin(nuxtApp => {
  const apolloClient = nuxtApp.$apollo?.defaultClient

  if (!apolloClient) {
    console.warn('Apollo client not available')

    return
  }

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const operationName = operation?.operationName
    const skipAuthFlow = AUTH_OP_SKIP.has(operationName)

    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        const code = err.extensions?.code

        switch (code) {
        case 'UNAUTHENTICATED':
        case 'KS_ACCESS_DENIED':
          if (skipAuthFlow) {
            // Middleware / logout flow owns this one.
            console.warn(`[Apollo] ${code} on ${operationName} — suppressed`)
            break
          }
          console.warn(`[Apollo] ${code} — session expired or invalid`)

          if (import.meta.client) {
            const authStore = useAuthStore()

            handleSessionExpired(authStore)
          }
          break

        case 'FORBIDDEN':
          console.warn('[Apollo] FORBIDDEN — access denied')
          if (import.meta.client && !isOnLoginPage()) {
            const from = window.location.pathname + window.location.search

            navigateTo(
              {
                path: '/account/unauthorized',
                query: { from },
              },
              { replace: true },
            )
          }
          break

        default:
          console.error('[Apollo] GraphQL Error:', err.message, err)
        }
      }
    }

    if (networkError) {
      console.error('[Apollo] Network Error:', networkError)

      // Treat network 401 the same as a graphql UNAUTHENTICATED — same
      // redirect target, same suppression rules, so the user sees one
      // consistent experience regardless of where in the stack the
      // failure originated.
      if (networkError.statusCode === 401 && !skipAuthFlow && import.meta.client) {
        const authStore = useAuthStore()

        handleSessionExpired(authStore)
      }
    }

    // No `forward(operation)` here on purpose: returning it would
    // retry the failing request through the link chain again. For
    // UNAUTHENTICATED that just guarantees a second failure (and
    // can loop forever on a flaky chain). We do the side effect
    // (clear auth + redirect) and let the original error
    // propagate so the caller can show its own snackbar.
  })

  // Prepend the error link to the existing chain. ("View As" is threaded through
  // a GraphQL `viewAsUserId` arg on the read resolvers, not a request header, so
  // there's no custom CORS header for a cross-origin preflight to block.)
  apolloClient.setLink(errorLink.concat(apolloClient.link))
})
